import { IonFooter, IonHeader, IonPage, IonTitle, useIonRouter } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BackButton } from "@/components/back-button";
import { Buttons } from "@/components/buttons";
import { Content } from "@/components/content";
import { Toolbar } from "@/components/toolbar";
import { useBoomerForm } from "@/form/boomer/hook";
import { useValidator } from "@/form/common/validator";
import { api } from "@/lib/backend";
import { type UpdateMeInput } from "@/lib/backend/api";
import { useExtractedMutation } from "@/lib/backend/utils/hooks";
import { useMe } from "@/lib/hooks/use-me";
import { Routes } from "@/lib/router";
import { logger } from "@/logger";
import { useStatusBarHeight } from "@/utils/use-status-bar-height";

const TOAST_KEY = "update-me";

export function SettingsPersonalInformationPage() {
  const { t } = useTranslation();
  const { me } = useMe();
  const router = useIonRouter();
  const statusBarHeight = useStatusBarHeight();

  const validator = useValidator();

  const updateMeMutation = useExtractedMutation({
    mutationKey: ["me", "update"],
    mutationFn: (input: UpdateMeInput) => api.me.updateMe(input),
    onMutate: () => {
      toast.loading(t("actions:update-me.loading"), { id: TOAST_KEY });
    },
    onSuccess: async () => {
      toast.success(t("actions:update-me.success"), { id: TOAST_KEY });
      window.location.href = Routes.Settings();
    },
    onError: (error) => {
      toast.error(t("actions:update-me.error"), { id: TOAST_KEY });
      logger.error(error);
    },
    onSettled: () => {
      setTimeout(() => toast.dismiss(TOAST_KEY), 2000);
    },
  });

  const [form, { Element: FormElement }] = useBoomerForm({
    defaultValues: {
      firstname: me?.firstname ?? "",
      lastname: me?.lastname ?? "",
      city: me?.employee.city ?? "",
      email: me?.email ?? "",
      phone: me?.employee.phone ?? "",
      addressLine: me?.employee.addressLine ?? "",
      state: me?.employee.state ?? "",
      zipCode: me?.employee.zipCode ?? "",
    },
    onSubmit: async ({ value }) => {
      try {
        await updateMeMutation.mutateAsync(value);
      } catch {
        // Error is already handled in mutation
      }
    },
  });

  return (
    <FormElement>
      <IonPage>
        <IonHeader>
          <Toolbar style={{ paddingTop: `calc(${statusBarHeight}px + 0.5rem)` }}>
            <Buttons slot="start">
              <BackButton defaultHref={Routes.Settings()} />
            </Buttons>
            <IonTitle>{t("routes:personal-information.title")}</IonTitle>
          </Toolbar>
        </IonHeader>
        <Content>
          <div className="flex flex-1 flex-col gap-6">
            <form.AppField
              name="firstname"
              validators={{
                onBlur: validator.required({ name: "firstname" }),
                onSubmit: validator.required({ name: "firstname" }),
              }}
              children={(field) => (
                <field.InputField required autocomplete="given-name" withPlaceholder />
              )}
            />
            <form.AppField
              name="lastname"
              validators={{
                onBlur: validator.required({ name: "lastname" }),
                onSubmit: validator.required({ name: "lastname" }),
              }}
              children={(field) => (
                <field.InputField required autocomplete="family-name" withPlaceholder />
              )}
            />
            <form.AppField
              name="addressLine"
              validators={{
                onBlur: validator.required({ name: "address" }),
                onSubmit: validator.required({ name: "address" }),
              }}
              children={(field) => (
                <field.InputField required autocomplete="street-address" withPlaceholder />
              )}
            />
            <form.AppField
              name="city"
              validators={{
                onBlur: validator.required({ name: "hometown" }),
                onSubmit: validator.required({ name: "hometown" }),
              }}
              children={(field) => (
                <field.InputField
                  name="hometown"
                  required
                  autocomplete="address-level2"
                  withPlaceholder
                />
              )}
            />
            <form.AppField
              name="state"
              validators={{
                onBlur: validator.required({ name: "state" }),
                onSubmit: validator.required({ name: "state" }),
              }}
              children={(field) => (
                <field.InputField required autocomplete="address-level1" withPlaceholder />
              )}
            />
            <form.AppField
              name="zipCode"
              validators={{
                onBlur: validator.required({ name: "zipCode" }),
                onSubmit: validator.required({ name: "zipCode" }),
              }}
              children={(field) => (
                <field.InputField required autocomplete="postal-code" withPlaceholder />
              )}
            />
            <form.AppField
              name="phone"
              validators={{
                onBlur: validator.required({ name: "phone" }),
                onSubmit: validator.required({ name: "phone" }),
              }}
              children={(field) => (
                <field.InputField type="tel" required autocomplete="tel" withPlaceholder />
              )}
            />
          </div>
        </Content>
        <IonFooter>
          <Toolbar>
            <form.SubmitButton />
          </Toolbar>
        </IonFooter>
      </IonPage>
    </FormElement>
  );
}
